import React from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { motion, type Variants } from 'framer-motion'
import { toast } from 'sonner'
import {
  Envelope,
  Lock,
  User,
  At,
  ArrowRight,
  ShieldCheck,
  Eye,
  EyeSlash,
  Check,
} from '@phosphor-icons/react'
import { DotLoader } from '@/components/ui/DotLoader'
import {
  authInputClass as inputClass,
  authInputStyle as inputStyle,
  errorBorderClass,
} from '@/lib/styleKit'
import { useAuthStore } from '@/stores/authStore'
import SocialAuthButtons from './SocialAuthButtons'

const registerSchema = z
  .object({
    full_name: z
      .string()
      .trim()
      .min(2, 'Full name must be at least 2 characters')
      .regex(
        /^[\p{L}][\p{L}\s'.-]*$/u,
        'Name can only contain letters, spaces, hyphens, apostrophes, and periods',
      ),
    username: z
      .string()
      .min(3, 'Username must be at least 3 characters')
      .regex(/^[a-zA-Z0-9_]+$/, 'Letters, numbers, and underscores only'),
    email: z.string().email('Please enter a valid email address'),
    password: z
      .string()
      .min(8, 'Password must be at least 8 characters')
      .regex(/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/, 'Use upper, lower, and a number'),
    confirmPassword: z.string(),
    terms: z.boolean().refine((val) => val === true, 'You must accept the terms'),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: "Passwords don't match",
    path: ['confirmPassword'],
  })

type RegisterFormData = z.infer<typeof registerSchema>

const easeOutExpo = [0.16, 1, 0.3, 1] as const

const formStageVariants: Variants = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: { staggerChildren: 0.06, delayChildren: 0.1 },
  },
}

const formItemVariants: Variants = {
  hidden: { opacity: 0, y: 10 },
  show: { opacity: 1, y: 0, transition: { duration: 0.5, ease: easeOutExpo } },
}

const shakeVariants: Variants = {
  idle: { x: 0 },
  error: {
    x: [0, -6, 6, -4, 4, 0],
    transition: { duration: 0.4, ease: 'easeInOut' },
  },
}

/**
 * The consent line gates submission on accepting both documents, so both links
 * must resolve to something readable — they used to fire a "coming soon" toast,
 * which asked the user to affirm documents they could not open.
 *
 * A deployment that hosts the real documents sets `VITE_TERMS_URL` /
 * `VITE_PRIVACY_URL` and we link straight out to them. Otherwise we fall back to
 * the in-app `/terms` and `/privacy` notices. Either way the destination opens
 * in a new tab so a half-filled registration form survives the detour.
 */
const TERMS_URL = (import.meta.env.VITE_TERMS_URL as string | undefined) || ''
const PRIVACY_URL = (import.meta.env.VITE_PRIVACY_URL as string | undefined) || ''

const legalLinkClass =
  'text-[#5e6ad2] hover:text-[#7170ff] dark:text-[#7170ff] dark:hover:text-[#828fff] transition-colors'
const legalLinkStyle = { fontWeight: 510 } as const

const LegalLink: React.FC<{ externalUrl: string; to: string; children: React.ReactNode }> = ({
  externalUrl,
  to,
  children,
}) =>
  externalUrl ? (
    <a
      href={externalUrl}
      target="_blank"
      rel="noopener noreferrer"
      className={legalLinkClass}
      style={legalLinkStyle}
    >
      {children}
    </a>
  ) : (
    <Link
      to={to}
      target="_blank"
      rel="noopener noreferrer"
      className={legalLinkClass}
      style={legalLinkStyle}
    >
      {children}
    </Link>
  )

type RegisterNavState = {
  // Carried back from /verify-otp after an OTP lockout, so the user doesn't
  // have to retype everything just to get a fresh code — see VerifyOtpPage's
  // LOCKED handling.
  prefill?: { full_name?: string; username?: string; email?: string }
  lockedOut?: boolean
}

const RegisterPage: React.FC = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const { register: registerUser, isLoading } = useAuthStore()
  const [showPassword, setShowPassword] = React.useState(false)
  const [showConfirm, setShowConfirm] = React.useState(false)
  // Single shared lock so picking a credential fills all fields together.
  const [formReadOnly, setFormReadOnly] = React.useState(true)
  const unlockForm = () => setFormReadOnly(false)

  const navState = location.state as RegisterNavState | null
  const prefill = navState?.prefill

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<RegisterFormData>({
    resolver: zodResolver(registerSchema),
    defaultValues: {
      full_name: prefill?.full_name ?? '',
      username: prefill?.username ?? '',
      email: prefill?.email ?? '',
    },
  })

  // Shown once, on arrival from a locked-out OTP screen — explains why the
  // user is back on the signup form instead of still entering a code.
  React.useEffect(() => {
    if (navState?.lockedOut) {
      toast.error('Too many incorrect codes. Please sign up again to get a fresh code.')
    }
    // Intentionally runs only on mount: this reads the location state react-router
    // handed us when we navigated here, not a value that should re-trigger the
    // toast on unrelated re-renders.
     
  }, [])

  const watchPassword = watch('password', '')

  const getPasswordStrength = (password: string) => {
    let strength = 0
    if (password.length >= 8) strength++
    if (/[a-z]/.test(password)) strength++
    if (/[A-Z]/.test(password)) strength++
    if (/\d/.test(password)) strength++
    if (/[^A-Za-z0-9]/.test(password)) strength++
    return strength
  }

  const passwordStrength = getPasswordStrength(watchPassword)
  const strengthLabels = ['Very Weak', 'Weak', 'Fair', 'Good', 'Strong']
  // Restrained, indigo-leaning ramp — keeps red-as-error / green-as-success functional,
  // but replaces orange/yellow generic-SaaS with indigo/violet tiers.
  const strengthColors = ['#ef4444', '#a3741f', '#5e6ad2', '#7170ff', '#10b981']
  const strengthLabel = passwordStrength > 0 ? strengthLabels[passwordStrength - 1] : 'None'

  // Space-separated ids are legal: the password field is described by its error
  // (when present) and by the strength meter (whenever the meter is rendered).
  const passwordDescribedBy =
    [errors.password ? 'password-error' : null, watchPassword ? 'password-strength' : null]
      .filter(Boolean)
      .join(' ') || undefined

  const onSubmit = async (data: RegisterFormData) => {
    try {
      const { confirmPassword, terms, ...registerData } = data
      const result = await registerUser(registerData)
      // full_name/username ride along so VerifyOtpPage can hand them back to
      // us if the OTP gets locked out — see RegisterNavState above.
      navigate('/verify-otp', {
        state: { email: result.email, full_name: data.full_name, username: data.username },
      })
    } catch (error) {
      // Error handled by store/toast
    }
  }

  return (
    <motion.div
      className="w-full"
      variants={formStageVariants}
      initial="hidden"
      animate="show"
    >
      {/* Override Chrome / Safari autofill preview styles — theme-aware */}
      <style>{`
        @keyframes onAutoFillStart { from {} to {} }
        .login-input:-webkit-autofill {
          animation-name: onAutoFillStart;
          animation-duration: 1ms;
        }
        /* Light defaults */
        .login-input:-webkit-autofill,
        .login-input:-webkit-autofill:hover,
        .login-input:-webkit-autofill:focus,
        .login-input:-webkit-autofill:active {
          -webkit-box-shadow: 0 0 0 1000px #ffffff inset !important;
          -webkit-text-fill-color: #0f1011 !important;
          caret-color: #0f1011 !important;
          border-color: rgba(15,16,17,0.08) !important;
          transition: background-color 9999s ease-in-out 0s, color 9999s ease-in-out 0s;
        }
        /* Dark overrides */
        .dark .login-input:-webkit-autofill,
        .dark .login-input:-webkit-autofill:hover,
        .dark .login-input:-webkit-autofill:focus,
        .dark .login-input:-webkit-autofill:active {
          -webkit-box-shadow: 0 0 0 1000px #141516 inset !important;
          -webkit-text-fill-color: #f7f8f8 !important;
          caret-color: #f7f8f8 !important;
          border-color: rgba(255,255,255,0.08) !important;
        }
        .login-input:-webkit-autofill:focus {
          border-color: rgba(113,112,255,0.55) !important;
        }
        .login-input:focus,
        .login-input:focus-visible {
          outline: none !important;
          box-shadow: none !important;
          --tw-ring-shadow: 0 0 #0000 !important;
          --tw-ring-offset-shadow: 0 0 #0000 !important;
        }
      `}</style>

      {/* Header */}
      <motion.div className="mb-8" variants={formItemVariants}>
        <h1
          className="text-[#0f1011] dark:text-[#f7f8f8]"
          style={{
            fontFamily: "'Instrument Serif', 'Iowan Old Style', 'Apple Garamond', Georgia, serif",
            fontSize: '36px',
            lineHeight: 1.05,
            letterSpacing: '-0.8px',
            fontWeight: 400,
          }}
        >
          Create your <span className="italic text-[#5e6ad2] dark:text-[#7170ff]">account</span>
        </h1>
        <p
          className="text-[#5f6166] dark:text-[#8a8f98] mt-2"
          style={{
            fontSize: '15px',
            lineHeight: 1.5,
            letterSpacing: '-0.165px',
            fontWeight: 400,
          }}
        >
          Get started with Beevelope in under a minute
        </p>
      </motion.div>

      {/* Register Form */}
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
        {/* Full Name */}
        <motion.div variants={formItemVariants}>
          <motion.div
            className="relative group"
            variants={shakeVariants}
            animate={errors.full_name ? 'error' : 'idle'}
          >
            <User
              className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#7a7d83] dark:text-[#62666d] group-focus-within:text-[#5e6ad2] dark:group-focus-within:text-[#7170ff] transition-colors pointer-events-none z-10"
              weight="regular"
            />
            <input
              id="full_name"
              type="text"
              autoComplete="name"
              placeholder="Full name"
              aria-label="Full name"
              aria-invalid={errors.full_name ? true : undefined}
              aria-describedby={errors.full_name ? 'full_name-error' : undefined}
              style={inputStyle}
              className={`${inputClass} ${errors.full_name ? errorBorderClass : ''}`}
              {...register('full_name')}
            />
          </motion.div>
          {errors.full_name && (
            <motion.p
              id="full_name-error"
              role="alert"
              className="mt-1.5 text-red-600 dark:text-red-400"
              style={{ fontSize: '13px', letterSpacing: '-0.13px', fontWeight: 400 }}
              initial={{ opacity: 0, y: -4 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.2, ease: easeOutExpo }}
            >
              {errors.full_name.message}
            </motion.p>
          )}
        </motion.div>

        {/* Username */}
        <motion.div variants={formItemVariants}>
          <motion.div
            className="relative group"
            variants={shakeVariants}
            animate={errors.username ? 'error' : 'idle'}
          >
            <At
              className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#7a7d83] dark:text-[#62666d] group-focus-within:text-[#5e6ad2] dark:group-focus-within:text-[#7170ff] transition-colors pointer-events-none z-10"
              weight="regular"
            />
            <input
              id="username"
              type="text"
              autoComplete="username"
              placeholder="Username"
              aria-label="Username"
              aria-invalid={errors.username ? true : undefined}
              aria-describedby={errors.username ? 'username-error' : undefined}
              style={inputStyle}
              className={`${inputClass} ${errors.username ? errorBorderClass : ''}`}
              {...register('username')}
            />
          </motion.div>
          {errors.username && (
            <motion.p
              id="username-error"
              role="alert"
              className="mt-1.5 text-red-600 dark:text-red-400"
              style={{ fontSize: '13px', letterSpacing: '-0.13px', fontWeight: 400 }}
              initial={{ opacity: 0, y: -4 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.2, ease: easeOutExpo }}
            >
              {errors.username.message}
            </motion.p>
          )}
        </motion.div>

        {/* Email */}
        <motion.div variants={formItemVariants}>
          <motion.div
            className="relative group"
            variants={shakeVariants}
            animate={errors.email ? 'error' : 'idle'}
          >
            <Envelope
              className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#7a7d83] dark:text-[#62666d] group-focus-within:text-[#5e6ad2] dark:group-focus-within:text-[#7170ff] transition-colors pointer-events-none z-10"
              weight="regular"
            />
            <input
              id="email"
              type="email"
              autoComplete="email"
              placeholder="Email address"
              aria-label="Email address"
              aria-invalid={errors.email ? true : undefined}
              aria-describedby={errors.email ? 'email-error' : undefined}
              readOnly={formReadOnly}
              style={inputStyle}
              className={`${inputClass} ${errors.email ? errorBorderClass : ''}`}
              {...register('email')}
              onFocus={unlockForm}
              onAnimationStart={(e) => {
                if ((e.nativeEvent as AnimationEvent).animationName === 'onAutoFillStart') {
                  unlockForm()
                }
              }}
            />
          </motion.div>
          {errors.email && (
            <motion.p
              id="email-error"
              role="alert"
              className="mt-1.5 text-red-600 dark:text-red-400"
              style={{ fontSize: '13px', letterSpacing: '-0.13px', fontWeight: 400 }}
              initial={{ opacity: 0, y: -4 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.2, ease: easeOutExpo }}
            >
              {errors.email.message}
            </motion.p>
          )}
        </motion.div>

        {/* Password */}
        <motion.div variants={formItemVariants}>
          <motion.div
            className="relative group"
            variants={shakeVariants}
            animate={errors.password ? 'error' : 'idle'}
          >
            <Lock
              className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#7a7d83] dark:text-[#62666d] group-focus-within:text-[#5e6ad2] dark:group-focus-within:text-[#7170ff] transition-colors pointer-events-none z-10"
              weight="regular"
            />
            <input
              id="password"
              type={showPassword ? 'text' : 'password'}
              autoComplete="new-password"
              placeholder="Password"
              aria-label="Password"
              aria-invalid={errors.password ? true : undefined}
              // The strength meter describes the field too, so it is announced
              // alongside (not instead of) the validation error.
              aria-describedby={passwordDescribedBy}
              readOnly={formReadOnly}
              style={inputStyle}
              className={`${inputClass} pr-10 ${errors.password ? errorBorderClass : ''}`}
              {...register('password')}
              onFocus={unlockForm}
              onAnimationStart={(e) => {
                if ((e.nativeEvent as AnimationEvent).animationName === 'onAutoFillStart') {
                  unlockForm()
                }
              }}
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              aria-label={showPassword ? 'Hide password' : 'Show password'}
              aria-pressed={showPassword}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-[#7a7d83] hover:text-[#0f1011] dark:text-[#62666d] dark:hover:text-[#d0d6e0] transition-colors focus:outline-none z-10"
            >
              {showPassword ? <EyeSlash className="h-4 w-4" weight="regular" /> : <Eye className="h-4 w-4" weight="regular" />}
            </button>
          </motion.div>
          {errors.password && (
            <motion.p
              id="password-error"
              role="alert"
              className="mt-1.5 text-red-600 dark:text-red-400"
              style={{ fontSize: '13px', letterSpacing: '-0.13px', fontWeight: 400 }}
              initial={{ opacity: 0, y: -4 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.2, ease: easeOutExpo }}
            >
              {errors.password.message}
            </motion.p>
          )}

          {/* Password Strength Indicator */}
          {watchPassword && (
            <motion.div
              className="mt-3"
              initial={{ opacity: 0, y: -4 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.25, ease: easeOutExpo }}
            >
              <div className="flex items-center gap-2.5 mb-2.5">
                {/* The 5 segments carry the value in colour alone, so the group
                    itself is the progressbar and the segments are decoration. */}
                <div
                  id="password-strength"
                  role="progressbar"
                  aria-label="Password strength"
                  aria-valuemin={0}
                  aria-valuemax={5}
                  aria-valuenow={passwordStrength}
                  aria-valuetext={strengthLabel}
                  className="flex gap-1 flex-1"
                >
                  {[1, 2, 3, 4, 5].map((level) => {
                    const active = level <= passwordStrength
                    return (
                      <div
                        key={level}
                        aria-hidden="true"
                        className={`h-[3px] flex-1 rounded-full transition-colors duration-200 ${
                          active ? '' : 'bg-black/[0.08] dark:bg-white/[0.06]'
                        }`}
                        style={active ? { backgroundColor: strengthColors[passwordStrength - 1] } : undefined}
                      />
                    )
                  })}
                </div>
                <span
                  // Duplicates `aria-valuetext` above — visual only.
                  aria-hidden="true"
                  className="text-[#5f6166] dark:text-[#8a8f98] shrink-0"
                  style={{
                    fontFamily: "'Berkeley Mono', ui-monospace, 'SF Mono', Menlo, monospace",
                    fontSize: '10px',
                    fontWeight: 400,
                    letterSpacing: '0.14em',
                    textTransform: 'uppercase',
                  }}
                >
                  {passwordStrength > 0 ? strengthLabels[passwordStrength - 1] : ''}
                </span>
              </div>

              {/* `role="list"` is explicit: `display: grid` drops list semantics
                  in Safari/VoiceOver. Met/unmet was signalled by icon colour and
                  weight alone — the sr-only suffix makes it audible. */}
              <ul
                role="list"
                aria-label="Password requirements"
                className="grid grid-cols-2 gap-x-3 gap-y-1.5"
              >
                {[
                  { test: watchPassword.length >= 8, text: '8+ characters' },
                  { test: /[a-z]/.test(watchPassword), text: 'Lowercase' },
                  { test: /[A-Z]/.test(watchPassword), text: 'Uppercase' },
                  { test: /\d/.test(watchPassword), text: 'Number' },
                ].map((req, index) => (
                  <li key={index} className="flex items-center gap-2">
                    <Check
                      aria-hidden="true"
                      className={`h-3 w-3 transition-colors duration-150 ${
                        req.test
                          ? 'text-[#5e6ad2] dark:text-[#7170ff]'
                          : 'text-black/20 dark:text-[#3e3e44]'
                      }`}
                      weight={req.test ? 'bold' : 'regular'}
                    />
                    <span
                      className={`transition-colors duration-150 ${
                        req.test
                          ? 'text-[#0f1011] dark:text-[#d0d6e0]'
                          : 'text-[#7a7d83] dark:text-[#62666d]'
                      }`}
                      style={{
                        fontSize: '12px',
                        letterSpacing: '-0.13px',
                        fontWeight: 400,
                      }}
                    >
                      {req.text}
                    </span>
                    <span className="sr-only">{req.test ? ' — met' : ' — not met'}</span>
                  </li>
                ))}
              </ul>
            </motion.div>
          )}
        </motion.div>

        {/* Confirm Password */}
        <motion.div variants={formItemVariants}>
          <motion.div
            className="relative group"
            variants={shakeVariants}
            animate={errors.confirmPassword ? 'error' : 'idle'}
          >
            <Lock
              className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#7a7d83] dark:text-[#62666d] group-focus-within:text-[#5e6ad2] dark:group-focus-within:text-[#7170ff] transition-colors pointer-events-none z-10"
              weight="regular"
            />
            <input
              id="confirmPassword"
              type={showConfirm ? 'text' : 'password'}
              autoComplete="new-password"
              placeholder="Confirm password"
              aria-label="Confirm password"
              aria-invalid={errors.confirmPassword ? true : undefined}
              aria-describedby={errors.confirmPassword ? 'confirmPassword-error' : undefined}
              readOnly={formReadOnly}
              style={inputStyle}
              className={`${inputClass} pr-10 ${errors.confirmPassword ? errorBorderClass : ''}`}
              {...register('confirmPassword')}
              onFocus={unlockForm}
              onAnimationStart={(e) => {
                if ((e.nativeEvent as AnimationEvent).animationName === 'onAutoFillStart') {
                  unlockForm()
                }
              }}
            />
            <button
              type="button"
              onClick={() => setShowConfirm(!showConfirm)}
              aria-label={
                showConfirm ? 'Hide password confirmation' : 'Show password confirmation'
              }
              aria-pressed={showConfirm}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-[#7a7d83] hover:text-[#0f1011] dark:text-[#62666d] dark:hover:text-[#d0d6e0] transition-colors focus:outline-none z-10"
            >
              {showConfirm ? <EyeSlash className="h-4 w-4" weight="regular" /> : <Eye className="h-4 w-4" weight="regular" />}
            </button>
          </motion.div>
          {errors.confirmPassword && (
            <motion.p
              id="confirmPassword-error"
              role="alert"
              className="mt-1.5 text-red-600 dark:text-red-400"
              style={{ fontSize: '13px', letterSpacing: '-0.13px', fontWeight: 400 }}
              initial={{ opacity: 0, y: -4 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.2, ease: easeOutExpo }}
            >
              {errors.confirmPassword.message}
            </motion.p>
          )}
        </motion.div>

        {/* Terms.
            The consent line used to be one <label> wrapping both links, so a
            click on "Terms of Service" also toggled the checkbox — the user
            could un-tick their consent on the way to reading it. The row is now
            a plain <div>: only the prose fragments are <label htmlFor="terms">,
            the anchors sit outside every label and cannot toggle anything.
            `aria-labelledby` on the checkbox points at the whole span so the
            accessible name is still the full sentence (aria-labelledby wins over
            the label elements, so the two fragments don't become the name). */}
        <motion.div variants={formItemVariants}>
          <div className="flex items-start gap-2.5 group">
            <input
              {...register('terms')}
              id="terms"
              type="checkbox"
              aria-labelledby="terms-consent-label"
              aria-invalid={errors.terms ? true : undefined}
              aria-describedby={errors.terms ? 'terms-error' : undefined}
              className="mt-[3px] h-3.5 w-3.5 rounded-sm bg-white border border-black/[0.15] dark:bg-white/[0.02] dark:border-white/[0.12] accent-[#5e6ad2] focus:outline-none focus:ring-2 focus:ring-[#5e6ad2]/30 focus:ring-offset-0 cursor-pointer shrink-0"
            />
            <span
              id="terms-consent-label"
              className="text-[#5f6166] group-hover:text-[#0f1011] dark:text-[#8a8f98] dark:group-hover:text-[#d0d6e0] transition-colors"
              style={{ fontSize: '13px', lineHeight: 1.5, letterSpacing: '-0.13px', fontWeight: 400 }}
            >
              <label htmlFor="terms" className="cursor-pointer">
                I agree to the
              </label>{' '}
              <LegalLink externalUrl={TERMS_URL} to="/terms">
                Terms of Service
              </LegalLink>{' '}
              <label htmlFor="terms" className="cursor-pointer">
                and
              </label>{' '}
              <LegalLink externalUrl={PRIVACY_URL} to="/privacy">
                Privacy Policy
              </LegalLink>
            </span>
          </div>
          {errors.terms && (
            <motion.p
              id="terms-error"
              role="alert"
              className="mt-1.5 text-red-600 dark:text-red-400 ml-6"
              style={{ fontSize: '13px', letterSpacing: '-0.13px', fontWeight: 400 }}
              initial={{ opacity: 0, y: -4 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.2, ease: easeOutExpo }}
            >
              {errors.terms.message}
            </motion.p>
          )}
        </motion.div>

        {/* Submit */}
        <motion.button
          type="submit"
          disabled={isLoading}
          aria-busy={isLoading}
          className="w-full h-10 rounded-md bg-[#5e6ad2] text-white inline-flex items-center justify-center gap-2 disabled:opacity-60 disabled:pointer-events-none focus:outline-none focus:ring-2 focus:ring-[#5e6ad2]/40 focus:ring-offset-2 focus:ring-offset-white dark:focus:ring-offset-[#0f1011]"
          style={{ fontSize: '14px', fontWeight: 510, letterSpacing: '-0.13px' }}
          variants={formItemVariants}
          whileHover={{ backgroundColor: '#7170ff', transition: { duration: 0.15 } }}
          whileTap={{ scale: 0.97, backgroundColor: '#5058c0' }}
        >
          {isLoading ? (
            <>
              <DotLoader size="sm" />
              <span>Creating account</span>
            </>
          ) : (
            <motion.span className="inline-flex items-center gap-2" whileHover="hover">
              Create account
              <motion.span
                variants={{ hover: { x: 3 } }}
                transition={{ duration: 0.2, ease: easeOutExpo }}
                className="inline-flex"
              >
                <ArrowRight className="h-4 w-4" weight="bold" />
              </motion.span>
            </motion.span>
          )}
        </motion.button>
      </form>

      {/* Security note — amber accent.
          The 10px caption used to be `#a3741f` in an inline style with no dark
          partner: 4.14:1 on the white card (under the 4.5:1 AA floor) and the
          same muted brown on the near-black card. Colour now lives in a
          light/dark class pair — `#8a6218` on #ffffff is 5.47:1, `#c9922e` on
          #0f1011 is 6.92:1. The glyph keeps the reserved trust amber `#f5a524`
          (decorative, and paired with the text it sits beside). */}
      <motion.div
        className="mt-6 flex items-center justify-center gap-1.5 text-[#8a6218] dark:text-[#c9922e]"
        style={{
          fontFamily: "'Berkeley Mono', ui-monospace, 'SF Mono', Menlo, monospace",
          fontSize: '10px',
          fontWeight: 400,
          letterSpacing: '0.14em',
          textTransform: 'uppercase',
        }}
        variants={formItemVariants}
      >
        <ShieldCheck className="h-3 w-3" weight="fill" style={{ color: '#f5a524' }} aria-hidden="true" />
        <span>Your data is encrypted end-to-end</span>
      </motion.div>

      {/* Social / enterprise SSO — hidden unless VITE_WORKOS_AUTH_ENABLED=true */}
      <div className="mt-6">
        <SocialAuthButtons redirect="/dashboard" />
      </div>

      {/* Divider */}
      <motion.div
        className="flex items-center gap-3 mt-7 mb-6"
        variants={formItemVariants}
      >
        <div className="flex-1 border-t border-black/[0.08] dark:border-white/[0.05]" />
        <span
          className="text-[#7a7d83] dark:text-[#62666d] shrink-0"
          style={{
            fontFamily: "'Berkeley Mono', ui-monospace, 'SF Mono', Menlo, monospace",
            fontSize: '10px',
            fontWeight: 400,
            letterSpacing: '0.18em',
            textTransform: 'uppercase',
          }}
        >
          Already a member
        </span>
        <div className="flex-1 border-t border-black/[0.08] dark:border-white/[0.05]" />
      </motion.div>

      <motion.button
        type="button"
        onClick={() => navigate('/login')}
        className="w-full h-10 rounded-md bg-white border border-black/[0.08] text-[#0f1011] hover:bg-[#f5f5f5] hover:border-black/[0.14] dark:bg-white/[0.02] dark:border-white/[0.08] dark:text-[#e2e4e7] dark:hover:bg-white/[0.04] dark:hover:border-white/[0.12] inline-flex items-center justify-center focus:outline-none focus:ring-2 focus:ring-[#5e6ad2]/30 focus:ring-offset-2 focus:ring-offset-white dark:focus:ring-offset-[#0f1011] transition-colors duration-150"
        style={{ fontSize: '14px', fontWeight: 510, letterSpacing: '-0.13px' }}
        variants={formItemVariants}
        whileTap={{ scale: 0.98 }}
      >
        Sign in instead
      </motion.button>
    </motion.div>
  )
}

export default RegisterPage
